import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getQueryFn, apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useLocation, useSearch } from "wouter";
import { TicketReply } from "./ticket-reply";
import { InternalNoteSection } from "./internal-note";
import { WorkRequestDetailContent } from "@/components/admin/intake/work-request-detail-content";
import { StatusBadge } from "@/components/shared/status-badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SandboxedHtml } from "@/components/shared/sandboxed-html";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Loader2,
  DollarSign,
  ExternalLink,
  FileText,
  CheckCircle2,
  CheckCheck,
  ShieldAlert,
  Trash2,
} from "lucide-react";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { formatDateTime, formatRelativeTime } from "@/lib/format";
import type {
  Ticket,
  Channel,
  InternalNote,
  Client,
  WorkRequest,
} from "@db/schema";
import {
  getWorkRequestCategoryLabel,
  getWorkRequestHeadline,
  getWorkRequestScopeSnippet,
  getWorkRequestSummaryText,
} from "@shared/work-request";

interface Message {
  id: string;
  from?: Array<{ name?: string; email?: string }>;
  to?: Array<{ name?: string; email?: string }>;
  date?: number;
  body?: string;
  subject?: string;
}

interface TicketDetailPanelProps {
  ticketId: string;
}

export function TicketDetailPanel({ ticketId }: TicketDetailPanelProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const [, navigate] = useLocation();
  const currentSearch = useSearch();
  const [workRequestSheetOpen, setWorkRequestSheetOpen] = useState(false);
  const [clearedReplyForTicket, setClearedReplyForTicket] = useState<string | null>(null);

  const { data: ticket, isLoading: ticketLoading } = useQuery<Ticket>({
    queryKey: ["/api/support/tickets", ticketId],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
  });

  const { data: messages, isLoading: messagesLoading } = useQuery<Message[]>({
    queryKey: [`/api/support/tickets/${ticketId}/messages`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
    refetchInterval: 30_000,
  });

  const { data: notes } = useQuery<InternalNote[]>({
    queryKey: [`/api/support/tickets/${ticketId}/internal-notes`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
  });

  const { data: workRequestResponse } = useQuery<{ workRequest: WorkRequest | null }>({
    queryKey: [`/api/support/tickets/${ticketId}/work-request`],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticketId,
  });

  const { data: channels } = useQuery<Channel[]>({
    queryKey: ["/api/support/channels"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/billing/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
  });
  const { data: services } = useQuery<
    Array<{
      id: string;
      clientId: string;
      contractId?: string | null;
      budgetId?: string | null;
      defaultServiceItemId?: string | null;
      customName?: string | null;
      product: { name: string };
    }>
  >({
    queryKey: ["/api/billing/client-services", ticket?.clientId ?? "all"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: !!ticket,
  });
  const { data: serviceItems } = useQuery<
    Array<{
      id: string;
      name: string;
      budget: { id: string; name: string };
      contract: { id: string; name: string } | null;
      client: { id: string; name: string };
    }>
  >({
    queryKey: ["/api/service-items"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const patchMutation = useMutation({
    mutationFn: async (data: Partial<Ticket>) => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/tickets/${ticketId}`,
        data,
      );
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      toast({ title: "Ticket updated" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to update ticket",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const clearNewReplyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/support/tickets/${ticketId}/clear-new-reply`,
      );
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      setClearedReplyForTicket(ticketId);
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
    },
  });

  const searchParams = new URLSearchParams(currentSearch);
  const statusFilter = searchParams.get("status");
  const normalizedSearch = searchParams.toString();
  const ticketsListHref = normalizedSearch ? `/tickets?${normalizedSearch}` : "/tickets";

  function navigateToListIfRemoved(nextStatus: Ticket["status"]) {
    if (
      nextStatus !== "closed" &&
      nextStatus !== "spam" &&
      nextStatus !== "deleted"
    ) {
      return;
    }

    if (
      statusFilter === "closed" ||
      statusFilter === "spam" ||
      statusFilter === "deleted"
    ) {
      return;
    }

    navigate(ticketsListHref);
  }

  const captureMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/support/tickets/${ticketId}/capture`,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      toast({ title: "Payment Captured" });
    },
    onError: (e) =>
      toast({
        title: "Capture failed",
        description: e.message,
        variant: "destructive",
      }),
  });

  const releaseMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "POST",
        `/api/support/tickets/${ticketId}/release`,
      );
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      toast({ title: "Payment Released/Waived" });
    },
    onError: (e) =>
      toast({
        title: "Release failed",
        description: e.message,
        variant: "destructive",
      }),
  });

  const acceptMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/support/tickets/${ticketId}/accept`);
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      toast({ title: "Ticket accepted" });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to accept ticket",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest(
        "PATCH",
        `/api/support/tickets/${ticketId}`,
        { status: "closed" },
      );
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      toast({ title: "Ticket closed" });
      navigateToListIfRemoved("closed");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to close ticket",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const spamMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/support/tickets/${ticketId}/spam`);
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      toast({ title: "Ticket marked as spam" });
      navigateToListIfRemoved("spam");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to mark ticket as spam",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/support/tickets/${ticketId}/delete`);
      return (await res.json()) as Ticket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/support/tickets", ticketId],
      });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
      queryClient.invalidateQueries({ queryKey: ["/api/support/tickets/counts"] });
      toast({ title: "Ticket moved to trash" });
      navigateToListIfRemoved("deleted");
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to delete ticket email",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  function refreshMessages() {
    queryClient.invalidateQueries({
      queryKey: [`/api/support/tickets/${ticketId}/messages`],
    });
    queryClient.invalidateQueries({
      queryKey: ["/api/support/tickets", ticketId],
    });
    queryClient.invalidateQueries({ queryKey: ["/api/support/tickets"] });
  }

  function refreshNotes() {
    queryClient.invalidateQueries({
      queryKey: [`/api/support/tickets/${ticketId}/internal-notes`],
    });
  }

  useEffect(() => {
    if (clearedReplyForTicket && clearedReplyForTicket !== ticketId) {
      setClearedReplyForTicket(null);
    }
  }, [clearedReplyForTicket, ticketId]);

  useEffect(() => {
    if (
      !ticket?.hasNewReply ||
      messagesLoading ||
      messages === undefined ||
      clearNewReplyMutation.isPending ||
      clearedReplyForTicket === ticketId
    ) {
      return;
    }

    clearNewReplyMutation.mutate();
  }, [
    ticket?.hasNewReply,
    messagesLoading,
    messages,
    clearNewReplyMutation,
    clearedReplyForTicket,
    ticketId,
  ]);

  if (ticketLoading) {
    return (
      <div className="flex flex-col h-full p-4 space-y-4">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-[200px] rounded-lg" />
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-muted-foreground">Ticket not found</p>
      </div>
    );
  }

  const channelName =
    channels?.find((c) => c.id === ticket.channelId)?.name ?? "\u2014";
  const clientName =
    clients?.find((c) => c.id === ticket.clientId)?.name ?? "\u2014";

  const workRequest = workRequestResponse?.workRequest ?? null;
  const filteredServices = (services ?? []).filter((service) =>
    ticket.clientId ? service.clientId === ticket.clientId : true,
  );
  const filteredServiceItems = (serviceItems ?? []).filter((item) =>
    ticket.clientId ? item.client.id === ticket.clientId : true,
  );
  const summaryHeadline = workRequest
    ? getWorkRequestHeadline({
        category: workRequest.category,
        company: workRequest.company,
        requestDetails: workRequest.requestDetails,
      })
    : null;
  const summaryText = workRequest
    ? getWorkRequestSummaryText(workRequest.requestDetails)
    : "";
  const scopeSnippet = workRequest
    ? getWorkRequestScopeSnippet(workRequest.aiScopeSummary)
    : null;

  // Derive default "to" from the linked work request or latest known sender.
  const defaultTo =
    workRequest?.email ??
    messages?.[messages.length - 1]?.from?.[0]?.email ??
    "";
  const hasLinkedEmailThread = Boolean(ticket.channelId && ticket.threadIds.length > 0);
  const isAcceptedByCurrentUser = ticket.assigneeUserId === user?.id;
  const quickActionPending =
    acceptMutation.isPending ||
    closeMutation.isPending ||
    spamMutation.isPending ||
    deleteMutation.isPending;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b px-4 py-3 space-y-2">
        {/* Row 1: Key + Subject + Status/Priority selects */}
        <div className="flex items-start gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              {ticket.key && (
                <span className="font-mono text-xs text-muted-foreground shrink-0">
                  {ticket.key}
                </span>
              )}
              {ticket.hasNewReply && (
                <Badge className="rounded-full bg-amber-100 text-amber-800 hover:bg-amber-100">
                  New reply
                </Badge>
              )}
            </div>
            <h2 className="text-base font-semibold font-heading leading-snug mt-0.5 line-clamp-2">
              {ticket.subject ?? "No subject"}
            </h2>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Select
              value={ticket.status}
              onValueChange={(val) => {
                const nextStatus = val as Ticket["status"];
                patchMutation.mutate({ status: nextStatus }, {
                  onSuccess: () => navigateToListIfRemoved(nextStatus),
                });
              }}
            >
              <SelectTrigger className="h-8 w-[130px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="closed">Closed</SelectItem>
                <SelectItem value="spam">Spam</SelectItem>
                <SelectItem value="deleted">Deleted</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={ticket.priority}
              onValueChange={(val) =>
                patchMutation.mutate({ priority: val as Ticket["priority"] })
              }
            >
              <SelectTrigger className="h-8 w-[110px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="P1">P1 Critical</SelectItem>
                <SelectItem value="P2">P2 High</SelectItem>
                <SelectItem value="P3">P3 Medium</SelectItem>
                <SelectItem value="P4">P4 Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            className="h-8"
            disabled={
              quickActionPending ||
              (ticket.status === "in_progress" && isAcceptedByCurrentUser)
            }
            onClick={() => acceptMutation.mutate()}
          >
            {acceptMutation.isPending ? (
              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-1.5 h-4 w-4" />
            )}
            Accept Ticket
          </Button>
          {hasLinkedEmailThread && (
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-slate-200 text-slate-700 hover:bg-slate-50 hover:text-slate-800"
                disabled={quickActionPending}
                onClick={() => closeMutation.mutate()}
              >
                {closeMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <CheckCheck className="mr-1.5 h-4 w-4" />
                )}
                Close
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-orange-200 text-orange-700 hover:bg-orange-50 hover:text-orange-800"
                disabled={quickActionPending}
                onClick={() => spamMutation.mutate()}
              >
                {spamMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <ShieldAlert className="mr-1.5 h-4 w-4" />
                )}
                Spam
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-8 border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
                disabled={quickActionPending}
                onClick={() => deleteMutation.mutate()}
              >
                {deleteMutation.isPending ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="mr-1.5 h-4 w-4" />
                )}
                Delete
              </Button>
            </>
          )}
          {isAcceptedByCurrentUser && (
            <Badge variant="secondary" className="rounded-full">
              Assigned to you
            </Badge>
          )}
        </div>

        {ticket.paymentStatus === "authorized" && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-yellow-50/50 border border-yellow-200">
            <div className="flex items-center gap-2 text-yellow-700">
              <DollarSign className="w-4 h-4" />
              <span className="text-xs font-medium">Payment Authorization Held</span>
            </div>
            <div className="flex-1" />
            <Button
              size="sm"
              variant="default"
              className="h-6 text-xs px-2"
              onClick={() => captureMutation.mutate()}
              disabled={captureMutation.isPending || releaseMutation.isPending}
            >
              {captureMutation.isPending && (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              )}
              Capture
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-xs px-2 border-yellow-300 text-yellow-800 hover:bg-yellow-100"
              onClick={() => releaseMutation.mutate()}
              disabled={captureMutation.isPending || releaseMutation.isPending}
            >
              {releaseMutation.isPending && (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              )}
              Release
            </Button>
          </div>
        )}

        {ticket.paymentStatus === "captured" && (
           <div className="flex items-center gap-2 text-xs text-green-600 font-medium px-1">
             <DollarSign className="w-3 h-3" /> Payment Captured
           </div>
        )}

        {/* Row 2: Info strip */}
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span>
            <span className="font-medium text-foreground/70">Channel:</span>{" "}
            {channelName}
          </span>
          <div className="flex items-center gap-1">
            <span className="font-medium text-foreground/70">Client:</span>
            <Select
              value={ticket.clientId?.toString() ?? "unassigned"}
              onValueChange={(val) => {
                const newId = val === "unassigned" ? null : parseInt(val);
                patchMutation.mutate({ clientId: newId } as any);
              }}
            >
              <SelectTrigger className="h-5 min-w-[100px] w-auto border-0 p-0 text-xs shadow-none hover:text-foreground bg-transparent focus:ring-0">
                <SelectValue placeholder="Guest" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">Guest (Unassigned)</SelectItem>
                {clients?.map((c) => (
                  <SelectItem key={c.id} value={c.id.toString()}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <span>
            <span className="font-medium text-foreground/70">Created:</span>{" "}
            {formatDateTime(ticket.createdAt)}
          </span>
          {ticket.lastMessageAt && (
            <span>
              <span className="font-medium text-foreground/70">Last msg:</span>{" "}
              {formatRelativeTime(ticket.lastMessageAt)}
            </span>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              Linked Service
            </p>
            <Select
              value={ticket.serviceId ?? "none"}
              onValueChange={(value) => {
                const selected = filteredServices.find((service) => service.id === value);
                patchMutation.mutate({
                  serviceId: value === "none" ? null : value,
                  contractId:
                    value === "none" ? null : selected?.contractId ?? null,
                  budgetId: value === "none" ? null : null,
                  serviceItemId: value === "none" ? null : null,
                } as any);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Assign service" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No service assigned</SelectItem>
                {filteredServices.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.customName || service.product.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground">
              Service Item
            </p>
            <Select
              value={ticket.serviceItemId ?? "none"}
              onValueChange={(value) => {
                const selected = filteredServiceItems.find((item) => item.id === value);
                patchMutation.mutate({
                  serviceItemId: value === "none" ? null : value,
                  budgetId: value === "none" ? null : selected?.budget.id ?? null,
                  contractId:
                    value === "none" ? ticket.contractId ?? null : selected?.contract?.id ?? null,
                } as any);
              }}
            >
              <SelectTrigger className="h-8 text-xs">
                <SelectValue placeholder="Assign service item" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No service item assigned</SelectItem>
                {filteredServiceItems.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.name} · {item.budget.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {workRequest && (
          <div className="rounded-lg border bg-muted/20 p-3 text-sm">
            <div className="flex flex-wrap items-start gap-2 justify-between">
              <div className="min-w-0 space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="secondary">
                    {getWorkRequestCategoryLabel(workRequest.category)}
                  </Badge>
                  {workRequest.isUrgent && (
                    <Badge className="bg-red-500/10 text-red-700 border-red-500/20">
                      Urgent
                    </Badge>
                  )}
                  {workRequest.aiEstimateBucket && (
                    <Badge variant="outline">
                      {workRequest.aiEstimateBucket}
                      {workRequest.aiEstimateHours
                        ? ` · ${workRequest.aiEstimateHours}h`
                        : ""}
                    </Badge>
                  )}
                </div>
                <div>
                  <p className="font-medium text-foreground">{summaryHeadline}</p>
                  <p className="text-xs text-muted-foreground">
                    {workRequest.fullName} · {workRequest.email} · {workRequest.company}
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Submitted {formatDateTime(workRequest.createdAt)}
                </p>
                {summaryText && (
                  <p className="text-xs text-muted-foreground">{summaryText}</p>
                )}
                {scopeSnippet && (
                  <p className="text-xs text-muted-foreground">{scopeSnippet}</p>
                )}
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8"
                  onClick={() => setWorkRequestSheetOpen(true)}
                >
                  <FileText className="w-3.5 h-3.5 mr-1.5" />
                  View full details
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    window.location.href = `/work-requests/${workRequest.id}`;
                  }}
                >
                  <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  Open work request
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {messagesLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-24 rounded-md" />
              ))}
            </div>
          ) : !messages?.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No messages yet.
            </p>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.id}
                className="rounded-md border p-3 space-y-1.5"
              >
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium">
                    <strong className="text-sm">{msg.from?.[0]?.name || "Unknown"}</strong> <span className="text-xs text-muted-foreground">&lt;{msg.from?.[0]?.email}&gt;</span>
                    <br />
                    <span className="text-xs text-muted-foreground">to: {msg.to?.[0]?.email}</span>
                  </span>
                  <span className="text-muted-foreground">
                    {formatDateTime(msg.date)}
                  </span>
                </div>
                {msg.subject && (
                  <strong className="text-md">{msg.subject}</strong>
                )}
                {msg.body && (
                  <SandboxedHtml
                    html={
                      /<[a-z][\s\S]*>/i.test(msg.body)
                        ? msg.body
                        : msg.body.replace(/\n/g, "<br />")
                    }
                  />
                )}
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      {/* Reply + Notes */}
      <div className="border-t">
        <Accordion type="multiple" defaultValue={["reply"]}>
          <AccordionItem value="reply" className="border-b">
            <AccordionTrigger className="px-4 py-2 text-xs hover:no-underline">
              Reply
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-3">
              <TicketReply
                ticketId={ticketId}
                defaultTo={defaultTo}
                onSent={refreshMessages}
              />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem value="notes" className="border-b-0">
            <AccordionTrigger className="px-4 py-2 text-xs hover:no-underline">
              Internal Notes ({notes?.length ?? 0})
            </AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              <InternalNoteSection
                ticketId={ticketId}
                notes={notes ?? []}
                onNoteAdded={refreshNotes}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {workRequest && (
        <Sheet open={workRequestSheetOpen} onOpenChange={setWorkRequestSheetOpen}>
          <SheetContent side="right" className="w-full sm:max-w-4xl overflow-y-auto">
            <SheetHeader className="mb-6 pr-8">
              <SheetTitle>Work Request Details</SheetTitle>
              <SheetDescription>
                Review the full submitted intake details linked to this ticket.
              </SheetDescription>
            </SheetHeader>
            <WorkRequestDetailContent workRequest={workRequest} />
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}
